import { PatientId, WithPatientId } from '../surgicalEvent.slice';
import { doctorBffApi, InvalidateArr } from '@/modules';
import { MedicalHoService } from './medicalHo.service';
import { MedicalHo, MedicalHoItem } from './medicalHo.model';
import { wrapError } from '../../error';
import { ID } from '@ianesthesie-kit/services';
import { constructProtocolCacheKey, ProtocolName } from '@/modules/protocol';

export const medicalHoApi = doctorBffApi.injectEndpoints({
  endpoints: (builder) => ({
    medicalHo: builder.query<MedicalHo, PatientId>({
      queryFn: ({ patientId }) =>
        MedicalHoService.getInstance(patientId).fetch().catch(wrapError),
      providesTags: (result, error, arg) => [
        { type: 'MedicalHo', id: arg.patientId }
      ]
    }),

    addHoItem: builder.mutation<
      MedicalHo,
      WithPatientId<Partial<MedicalHoItem>>
    >({
      queryFn: ({ patientId, ...item }) =>
        MedicalHoService.getInstance(patientId).addItem(item).catch(wrapError),
      invalidatesTags: (result, error, { patientId, qcode }) =>
        addRelevantProtocols({ qcode }, patientId, [
          { type: 'MedicalHo', id: patientId },
          'Dashboard',
          { type: 'SurgeryInfo', id: patientId } // for ASA prediction update
        ])
    }),

    deleteHoItem: builder.mutation<MedicalHo, WithPatientId<MedicalHoItem>>({
      queryFn: ({ patientId, ...item }) =>
        MedicalHoService.getInstance(patientId)
          .deleteItem(item)
          .catch(wrapError),
      invalidatesTags: (result, error, { patientId, qcode }) =>
        addRelevantProtocols({ qcode }, patientId, [
          { type: 'MedicalHo', id: patientId },
          'Dashboard',
          { type: 'SurgeryInfo', id: patientId } // for ASA prediction update
        ])
    })
  }),
  overrideExisting: false
});

function addRelevantProtocols(
  mhoItem: Partial<MedicalHoItem>,
  patientId: ID,
  arr: InvalidateArr
) {
  const qcode = mhoItem?.qcode;
  if (!qcode) {
    return arr;
  }
  const initialLength = arr.length;
  if (qcode === 'CoronaryBypass') {
    arr.push({
      type: 'Protocol',
      id: constructProtocolCacheKey({
        patientId,
        protocol: ProtocolName.CARDIAC_STRESS_TEST
      })
    });
  } else if (
    qcode === 'Smoker' ||
    [
      'SmokerWantToStop',
      'SmokerCigPerDay',
      'SmokerFirstCigDelay',
      'SmokerIntensity'
    ].includes(qcode)
  ) {
    arr.push({
      type: 'Protocol',
      id: constructProtocolCacheKey({
        patientId,
        protocol: ProtocolName.NICOTINE
      })
    });
  }

  if (initialLength !== arr.length) {
    // if any tag is added, also add this one
    arr.push({ type: 'ProtocolStatuses', id: patientId });
  }

  return arr;
}

export const {
  useMedicalHoQuery,
  useDeleteHoItemMutation,
  useAddHoItemMutation
} = medicalHoApi;
